import {
  CanvasAndUser,
  Role,
  SITE_DOMAINS,
  Subscription,
} from '@ritabygglov/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';
import { canvasService } from '../../../apiServices/CanvasService';
import { userService } from '../../../apiServices/UserService';
import { Navbar } from '../../../components/Navbar/Navbar';
import {
  Table,
  TableBody,
  TableHead,
  TableWrapper,
} from '../../../components/Table/Table';
import { useModal } from '../../../context/Modal/useModal';
import { useToast } from '../../../context/Toast/useToast';
import { UserData } from '../../../context/User/useUser';
import { AdminCopyProjectModal } from '../Projects/AdminCopyProjectModal';
import { GarbageCollectModal } from '../Projects/GarbageCollectModal';

const getActiveSubscription = (subscriptions: Subscription[]) => {
  return subscriptions.find(
    (sub) => sub.status === 'active' || sub.status === 'trialing'
  );
};

type SortField = 'createdAt' | 'updatedAt';

type SortConfig = {
  field: SortField;
  order: 'asc' | 'desc';
};

const UserProjectsTable = ({
  projects,
  sortConfig,
  onSort,
  onGarbageCollect,
  onAdminCopy,
}: {
  projects: CanvasAndUser[];
  sortConfig: SortConfig;
  onSort: (field: SortField) => void;
  onGarbageCollect: (project: CanvasAndUser) => void;
  onAdminCopy: (project: CanvasAndUser) => void;
}) => {
  const intl = useIntl();
  const getSortIndicator = (field: SortField) => {
    if (sortConfig.field !== field) return ' ↕';
    return sortConfig.order === 'asc' ? ' ↑' : ' ↓';
  };

  return (
    <TableWrapper>
      <Table>
        <colgroup>
          <col style={{ width: '200px' }} />
          <col style={{ width: '100px' }} />
          <col style={{ width: '120px' }} />
          <col style={{ width: '140px' }} />
          <col style={{ width: '120px' }} />
        </colgroup>
        <TableHead>
          <tr>
            <th>{intl.formatMessage({ id: 'admin.projects.nameHeader' })}</th>
            <th>{intl.formatMessage({ id: 'admin.projects.changesCount' })}</th>
            <th
              onClick={() => onSort('createdAt')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              {intl.formatMessage({ id: 'admin.projects.createdHeader' })}
              {getSortIndicator('createdAt')}
            </th>
            <th
              onClick={() => onSort('updatedAt')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              {intl.formatMessage({ id: 'admin.projects.updatedHeader' })}
              {getSortIndicator('updatedAt')}
            </th>
            <th>{intl.formatMessage({ id: 'admin.projects.actions' })}</th>
          </tr>
        </TableHead>
        <TableBody>
          {projects.map((project, index) => (
            <tr key={index}>
              <td title={project.name}>
                <Link
                  to={`/projects/export/${project.canvasId}`}
                  target="_blank"
                >
                  {project.name}
                </Link>
              </td>
              <td>{project.changeCount ?? 0}</td>
              <td>
                {new Date(project.createdAt).toLocaleString('sv-SV', {
                  dateStyle: 'short',
                  timeStyle: 'short',
                })}
              </td>
              <td>
                {new Date(project.updatedAt).toLocaleString('sv-SV', {
                  dateStyle: 'short',
                  timeStyle: 'short',
                })}
              </td>
              <td>
                {(() => {
                  const hasNoChanges = !project.changeCount;
                  return (
                    <>
                      <button
                        onClick={() => onGarbageCollect(project)}
                        disabled={hasNoChanges}
                        style={{
                          padding: '4px 8px',
                          fontSize: '12px',
                          cursor: hasNoChanges ? 'not-allowed' : 'pointer',
                          opacity: hasNoChanges ? 0.5 : 1,
                          marginRight: '4px',
                        }}
                        title={
                          hasNoChanges
                            ? intl.formatMessage({
                                id: 'admin.projects.noChangesToClear',
                              })
                            : intl.formatMessage({
                                id: 'admin.projects.clearChanges',
                              })
                        }
                      >
                        🗑️ GC
                      </button>
                      <button
                        onClick={() => onAdminCopy(project)}
                        style={{
                          padding: '4px 8px',
                          fontSize: '12px',
                          cursor: 'pointer',
                        }}
                        title={intl.formatMessage({
                          id: 'admin.copyProject.title',
                        })}
                      >
                        {intl.formatMessage({
                          id: 'admin.copyProject.copyButton',
                        })}
                      </button>
                    </>
                  );
                })()}
              </td>
            </tr>
          ))}
        </TableBody>
      </Table>
    </TableWrapper>
  );
};

export const UserProjects = () => {
  const { userId: userIdParam = '' } = useParams<{ userId: string }>();
  const userId = parseInt(userIdParam, 10);

  const [user, setUser] = useState<UserData | null>(null);
  const [projects, setProjects] = useState<CanvasAndUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [adminUsers, setAdminUsers] = useState<UserData[]>([]);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'createdAt',
    order: 'desc',
  });
  const { addToast } = useToast();
  const { onOpen } = useModal();
  const intl = useIntl();

  const handleSort = (field: SortField) => {
    setSortConfig((current) => ({
      field,
      order:
        current.field === field && current.order === 'desc' ? 'asc' : 'desc',
    }));
  };

  const sortedProjects = useMemo(() => {
    return [...projects].sort((a, b) => {
      const aTime = new Date(a[sortConfig.field]).getTime();
      const bTime = new Date(b[sortConfig.field]).getTime();
      return sortConfig.order === 'asc' ? aTime - bTime : bTime - aTime;
    });
  }, [projects, sortConfig]);

  const fetchProjects = useCallback(async () => {
    if (Number.isNaN(userId)) return;
    setLoading(true);
    try {
      const { user, projects } = await canvasService.getUserProjects(userId);
      setUser(user);
      setProjects(projects);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : intl.formatMessage({ id: 'admin.userProjects.fetchError' });
      addToast({
        message: errorMessage,
        type: 'error',
      });
      console.error('Failed to fetch user projects:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, addToast, intl]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    const params = new URLSearchParams({
      page: '1',
      limit: '100',
      role: Role.Admin,
    }).toString();
    userService.getUsers(params).then(({ users }) => {
      setAdminUsers(users);
    });
  }, []);

  const handleGarbageCollect = (project: CanvasAndUser) => {
    onOpen(
      <GarbageCollectModal
        canvasId={project.canvasId}
        canvasName={project.name}
        changeCount={project.changeCount ?? 0}
        onSuccess={fetchProjects}
      />
    );
  };

  const handleAdminCopy = (project: CanvasAndUser) => {
    onOpen(
      <AdminCopyProjectModal
        canvasId={project.canvasId}
        canvasName={project.name}
        ownerUserId={project.userId}
        ownerEmail={user?.email ?? ''}
        adminUsers={adminUsers}
        onSuccess={fetchProjects}
      />
    );
  };

  const activeSub = user?.subscriptions
    ? getActiveSubscription(user.subscriptions)
    : undefined;

  return (
    <div className="admin-page">
      <Navbar />
      <h1>
        {user
          ? intl.formatMessage(
              { id: 'admin.userProjects.title' },
              { name: user.name }
            )
          : intl.formatMessage({ id: 'admin.userProjects.titleFallback' })}
        {activeSub && <span> ⭐</span>}
      </h1>

      {user && (
        <div style={{ marginBottom: '20px' }}>
          <p>
            {user.email} <br />
            {intl.formatMessage(
              { id: 'admin.userProjects.site' },
              { site: SITE_DOMAINS[user.site] }
            )}
          </p>
          <p>
            {intl.formatMessage(
              { id: 'admin.userProjects.total' },
              { count: <strong>{projects.length}</strong> }
            )}
          </p>
        </div>
      )}

      {loading && projects.length === 0 ? (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          {intl.formatMessage({ id: 'admin.userProjects.loading' })}
        </div>
      ) : projects.length === 0 ? (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          {intl.formatMessage({ id: 'admin.userProjects.empty' })}
        </div>
      ) : (
        <UserProjectsTable
          projects={sortedProjects}
          sortConfig={sortConfig}
          onSort={handleSort}
          onGarbageCollect={handleGarbageCollect}
          onAdminCopy={handleAdminCopy}
        />
      )}
    </div>
  );
};

export default UserProjects;
